import {
  useState,
  useRef,
  useEffect,
  useCallback,
  useMemo,
  memo
} from 'react';
import { motion, AnimatePresence } from 'framer-motion';

interface Option {
  value: string;
  label: string;
}

interface SearchableSelectProps {
  label: string;
  name: string;
  value: string;
  onChange: (name: string, value: string) => void;
  options: Option[];
  required?: boolean;
  placeholder?: string;
}

const SearchableSelect = memo(({
  label,
  name,
  value,
  onChange,
  options,
  required = false,
  placeholder = 'Search...'
}: SearchableSelectProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [activeIndex, setActiveIndex] = useState(0);

  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Get the display label for the selected value
  const selectedOption = options.find(o => o.value === value);
  const displayValue = selectedOption?.label ?? '';

  // Memoized filtering for performance
  const filteredOptions = useMemo(() => {
    return options.filter(o =>
      o.label.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [options, searchTerm]);

  // Reset active index when filtered options change
  useEffect(() => {
    setActiveIndex(0);
  }, [filteredOptions]);

  // Handle click outside to close dropdown
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (!containerRef.current?.contains(e.target as Node)) {
        setIsOpen(false);
        setSearchTerm('');
        setActiveIndex(0);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const selectOption = useCallback((option: Option) => {
    onChange(name, option.value);
    setIsOpen(false);
    setSearchTerm('');
    setActiveIndex(0);
  }, [name, onChange]);

  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (!isOpen) {
      if (e.key === 'ArrowDown' || e.key === 'Enter') {
        e.preventDefault();
        setIsOpen(true);
      }
      return;
    }

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setActiveIndex(i => Math.min(i + 1, filteredOptions.length - 1));
        break;
      case 'ArrowUp':
        e.preventDefault();
        setActiveIndex(i => Math.max(i - 1, 0));
        break;
      case 'Enter':
        e.preventDefault();
        if (filteredOptions[activeIndex]) {
          selectOption(filteredOptions[activeIndex]);
        }
        break;
      case 'Escape':
        e.preventDefault();
        setIsOpen(false);
        setSearchTerm('');
        setActiveIndex(0);
        break;
    }
  }, [filteredOptions, activeIndex, isOpen, selectOption]);

  const handleInputFocus = useCallback(() => {
    setIsOpen(true);
    setSearchTerm('');
  }, []);

  const handleInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
    if (!isOpen) setIsOpen(true);
  }, [isOpen]);

  return (
    <div ref={containerRef} className="relative w-full">
      <label 
        htmlFor={name} 
        className="block text-sm font-medium text-gray-700 mb-1"
      >
        {label} {required && <span className="text-red-500">*</span>}
      </label>

      <div className="relative">
        <input
          ref={inputRef}
          id={name}
          name={name}
          type="text"
          role="combobox"
          aria-expanded={isOpen}
          aria-controls={`${name}-listbox`}
          aria-autocomplete="list"
          aria-activedescendant={isOpen && filteredOptions[activeIndex] 
            ? `${name}-option-${filteredOptions[activeIndex].value}` 
            : undefined
          }
          value={isOpen ? searchTerm : displayValue}
          placeholder={isOpen ? placeholder : (displayValue || placeholder)}
          onFocus={handleInputFocus}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          required={required && !value}
          autoComplete="off"
          className="w-full px-3 py-2 pr-10 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#FA2C37] focus:border-[#FA2C37] outline-none transition-colors bg-white"
        />

        {/* Dropdown Arrow */}
        <div
          className="absolute inset-y-0 right-0 flex items-center pr-3 cursor-pointer"
          onClick={() => {
            setIsOpen(!isOpen);
            if (!isOpen) inputRef.current?.focus();
          }}
          aria-hidden="true"
        >
          <motion.svg
            animate={{ rotate: isOpen ? 180 : 0 }}
            transition={{ duration: 0.2 }}
            className="w-5 h-5 text-gray-400"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </motion.svg>
        </div>
      </div>

      {/* Dropdown Options */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            id={`${name}-listbox`}
            role="listbox"
            aria-label={label}
            initial={{ opacity: 0, y: -8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            transition={{ duration: 0.15, ease: 'easeOut' }}
            className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-auto"
          >
            {filteredOptions.length > 0 ? (
              filteredOptions.map((option, index) => (
                <div
                  key={option.value}
                  id={`${name}-option-${option.value}`}
                  role="option"
                  aria-selected={option.value === value}
                  onMouseDown={() => selectOption(option)}
                  onMouseEnter={() => setActiveIndex(index)}
                  className={`px-4 py-3 cursor-pointer transition-colors ${
                    index === activeIndex
                      ? 'bg-[#FA2C37]/10 text-[#FA2C37]'
                      : option.value === value
                        ? 'bg-[#FA2C37]/5 text-[#FA2C37] font-medium'
                        : 'hover:bg-gray-50'
                  }`}
                >
                  {option.label}
                </div>
              ))
            ) : (
              <div className="px-4 py-3 text-center text-gray-500">
                No results found
              </div>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
});

SearchableSelect.displayName = 'SearchableSelect';

export default SearchableSelect;
